import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';

export default function AboutPage() {
  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pt: 12 }}>
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              mb: 2,
              background: 'linear-gradient(135deg, #1E23FE 0%, #0D9E6F 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            About SimplyPayroll
          </Typography>
          <Typography
            variant="h5"
            sx={{ color: 'text.secondary', maxWidth: 700, mx: 'auto', mb: 4 }}
          >
            Simplifying contractor payroll management for small businesses
          </Typography>
        </Box>

        {/* Mission Statement */}
        <Card elevation={2} sx={{ mb: 6 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
              Our Mission
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 2 }}
            >
              SimplyPayroll was built to solve a common problem faced by small
              businesses: managing contractor payroll is complicated,
              time-consuming, and error-prone. Traditional payroll software is
              often overcomplicated with features designed for large
              enterprises.
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}
            >
              We believe small businesses deserve simple, affordable tools that
              just work. SimplyPayroll focuses on what matters most: tracking
              hours, calculating payments, and generating accurate 1099 forms at
              tax time. No complexity, no unnecessary features—just the
              essentials done right.
            </Typography>
          </CardContent>
        </Card>

        {/* Core Values */}
        <Typography
          variant="h4"
          sx={{ fontWeight: 600, mb: 4, textAlign: 'center' }}
        >
          Our Core Values
        </Typography>
        <Grid container spacing={4} sx={{ mb: 8 }}>
          <Grid item xs={12} md={4}>
            <Card elevation={2} sx={{ height: '100%', textAlign: 'center' }}>
              <CardContent sx={{ p: 4 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'primary.main',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  <SpeedIcon sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                  Simplicity First
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ lineHeight: 1.7 }}
                >
                  We eliminate unnecessary complexity. Every feature is designed
                  to be intuitive and straightforward, saving you time and
                  reducing errors.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card elevation={2} sx={{ height: '100%', textAlign: 'center' }}>
              <CardContent sx={{ p: 4 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'success.main',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  <TrendingUpIcon sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                  Small Business Focus
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ lineHeight: 1.7 }}
                >
                  Built specifically for small businesses managing 1099
                  contractors. We understand your needs because we've been there
                  ourselves.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card elevation={2} sx={{ height: '100%', textAlign: 'center' }}>
              <CardContent sx={{ p: 4 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'info.main',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  <SecurityIcon sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                  Security & Compliance
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ lineHeight: 1.7 }}
                >
                  Your data is encrypted and secure. We stay up-to-date with IRS
                  requirements so your 1099 forms are always compliant.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* The Story */}
        <Card elevation={2} sx={{ mb: 8 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
              Our Story
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 2 }}
            >
              SimplyPayroll started in 2024 when our founder was running a small
              consulting business with several contractors. After struggling
              with spreadsheets and expensive enterprise software, they decided
              there had to be a better way.
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 2 }}
            >
              The solution? A focused tool that does three things exceptionally
              well: track contractor hours, calculate payments, and generate
              1099 forms. No bloated features, no confusing interfaces—just the
              essentials that small businesses actually need.
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}
            >
              Today, SimplyPayroll helps hundreds of small businesses manage
              their contractor payroll with confidence. We're proud to be part
              of your success story.
            </Typography>
          </CardContent>
        </Card>

        {/* CTA */}
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
            Ready to simplify your payroll?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Join hundreds of small businesses managing contractor payroll the
            simple way.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <a href={`${process.env.REACT_APP_APP_URL || 'https://localhost:6001'}/register`} style={{ textDecoration: 'none' }}>
              <Typography
                component="span"
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  display: 'inline-block',
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                Get Started Free
              </Typography>
            </a>
            <a href="/contact" style={{ textDecoration: 'none' }}>
              <Typography
                component="span"
                sx={{
                  border: '2px solid',
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  display: 'inline-block',
                  '&:hover': { bgcolor: 'primary.50' },
                }}
              >
                Contact Us
              </Typography>
            </a>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
